import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { backupAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Download, Key, Copy, Check, AlertTriangle, RefreshCw,
  Filter, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { encryptBackup, hashRecoveryPhrase, decryptContent } from '@/lib/crypto';
import { formatDistanceToNow } from 'date-fns';
import { copyToClipboard } from '@/lib/clipboard';

export default function BackupExportPanel() {
  const { generateRecoveryKey, recordBackup, hasRecoveryKey, lastBackupAt, user, encryptionKey } = useAuth();
  const { notebooks, tags } = useApp();

  // Recovery key state
  const [loading, setLoading] = useState(false);
  const [showRecoveryKey, setShowRecoveryKey] = useState(false);
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [copied, setCopied] = useState(false);

  // Export state
  const [exportEncrypted, setExportEncrypted] = useState(true);
  const [filterNotebook, setFilterNotebook] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const handleGenerateRecoveryKey = async () => {
    setLoading(true);
    try {
      const phrase = await generateRecoveryKey();
      setRecoveryPhrase(phrase);
      setShowRecoveryKey(true);
      toast.success('Recovery key generated!');
    } catch { toast.error('Failed to generate recovery key'); }
    setLoading(false);
  };

  const handleCopyRecoveryKey = async () => {
    const result = await copyToClipboard(recoveryPhrase);
    if (result.success) { setCopied(true); toast.success('Copied!'); setTimeout(() => setCopied(false), 2000); }
    else { toast.info('Select the text above and copy manually (Ctrl+C)'); }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterNotebook) params.notebook_id = filterNotebook;
      if (filterTag) params.tag_id = filterTag;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;

      const res = await backupAPI.exportBundle(params);
      let bundle = res.data;

      if (encryptionKey && bundle.data.notes) {
        const decrypted = await Promise.all(
          bundle.data.notes.map(async (note) => {
            if (note.is_encrypted && note.encryption_iv && note.content) {
              try {
                const dc = await decryptContent(note.content, note.encryption_iv, encryptionKey);
                return { ...note, content: dc, is_encrypted: false, encryption_iv: null, encryption_salt: null };
              } catch { return note; }
            }
            return note;
          })
        );
        bundle = { ...bundle, data: { ...bundle.data, notes: decrypted } };
      }

      let fileContent;
      let filename;

      if (exportEncrypted) {
        if (!hasRecoveryKey) { toast.error('Generate a recovery key first'); setLoading(false); return; }
        const key = prompt('Enter your 24-word recovery key to encrypt the backup:');
        if (!key?.trim()) { toast.error('Recovery key required'); setLoading(false); return; }
        const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, ' ');
        const keyHash = await hashRecoveryPhrase(normalizedKey);
        if (keyHash !== user.recovery_key_hash) { toast.error('Invalid recovery key'); setLoading(false); return; }
        const encrypted = await encryptBackup(bundle, normalizedKey);
        fileContent = JSON.stringify(encrypted, null, 2);
        filename = `vaultnotes-backup-${new Date().toISOString().split('T')[0]}.encrypted.json`;
      } else {
        fileContent = JSON.stringify(bundle, null, 2);
        filename = `vaultnotes-backup-${new Date().toISOString().split('T')[0]}.json`;
      }

      const blob = new Blob([fileContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      await recordBackup();
      toast.success(`Backup exported (${bundle.manifest.counts.notes} notes)`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export backup');
    }
    setLoading(false);
  };

  const isFiltered = filterNotebook || filterTag || filterDateFrom || filterDateTo;

  return (
    <>
      {/* Recovery Key */}
      <div className="p-3 bg-secondary/30 rounded-lg border border-border/40 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-accent-amber" />
            <Label className="text-sm font-medium">Recovery Key</Label>
          </div>
          {hasRecoveryKey ? (
            <span className="text-xs text-green-500 flex items-center gap-1"><Check className="w-3 h-3" /> Set</span>
          ) : (
            <span className="text-xs text-muted-foreground">Not set</span>
          )}
        </div>

        {!hasRecoveryKey && !showRecoveryKey && (
          <Button onClick={handleGenerateRecoveryKey} size="sm" variant="outline" className="w-full" disabled={loading} data-testid="generate-recovery-key-btn">
            <Key className="w-3.5 h-3.5 mr-1.5" /> Generate Recovery Key
          </Button>
        )}

        {showRecoveryKey && recoveryPhrase && (
          <div className="space-y-3 animate-fade-in">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-500 leading-relaxed">
                  Write down these 24 words. This is the ONLY time you'll see them. Without this key, encrypted backups cannot be recovered.
                </p>
              </div>
              <div className="bg-background/50 p-3 rounded font-mono text-xs leading-relaxed select-all" data-testid="recovery-phrase">{recoveryPhrase}</div>
              <div className="flex gap-2 mt-2">
                <Button onClick={handleCopyRecoveryKey} size="sm" variant="outline" className="flex-1" data-testid="copy-recovery-key-btn">
                  {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button onClick={() => { setShowRecoveryKey(false); setRecoveryPhrase(''); }} size="sm" variant="secondary" className="flex-1" data-testid="done-recovery-key-btn">
                  I've Saved It
                </Button>
              </div>
            </div>
          </div>
        )}

        {hasRecoveryKey && !showRecoveryKey && (
          <Button onClick={handleGenerateRecoveryKey} size="sm" variant="ghost" className="w-full text-muted-foreground" disabled={loading} data-testid="regenerate-recovery-key-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate Key (invalidates old backups)
          </Button>
        )}
      </div>

      {/* Export Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Download className="w-4 h-4 text-accent-cyan" />
          <h3 className="text-sm font-semibold">Export Backup</h3>
        </div>

        <div className="flex items-center justify-between">
          <Label className="text-xs">Encrypt backup</Label>
          <Switch checked={exportEncrypted} onCheckedChange={setExportEncrypted} data-testid="export-encrypt-toggle" />
        </div>

        {!exportEncrypted && (
          <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-500 flex items-start gap-2" data-testid="export-unencrypted-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Unencrypted backups contain your notes in plain text. Only use for personal transfers on trusted devices.</span>
          </div>
        )}

        <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="export-filter-toggle">
          <Filter className="w-3.5 h-3.5" />
          {isFiltered ? 'Filters active' : 'Add filters (optional)'}
        </button>

        {showFilters && (
          <div className="p-3 bg-secondary/20 rounded-lg border border-border/30 space-y-3 animate-fade-in" data-testid="export-filters">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Notebook</Label>
              <select value={filterNotebook} onChange={(e) => setFilterNotebook(e.target.value)} className="w-full h-8 px-2 text-xs bg-input border border-border rounded outline-none" data-testid="export-filter-notebook">
                <option value="">All notebooks</option>
                {notebooks.map((nb) => <option key={nb.id} value={nb.id}>{nb.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Tag</Label>
              <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} className="w-full h-8 px-2 text-xs bg-input border border-border rounded outline-none" data-testid="export-filter-tag">
                <option value="">All tags</option>
                {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">From</Label>
                <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="w-full h-8 px-2 text-xs bg-input border border-border rounded outline-none" data-testid="export-filter-date-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">To</Label>
                <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="w-full h-8 px-2 text-xs bg-input border border-border rounded outline-none" data-testid="export-filter-date-to" />
              </div>
            </div>
            {isFiltered && (
              <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { setFilterNotebook(''); setFilterTag(''); setFilterDateFrom(''); setFilterDateTo(''); }}>
                Clear Filters
              </Button>
            )}
          </div>
        )}

        {lastBackupAt && (
          <p className="text-xs text-muted-foreground">
            Last backup: {formatDistanceToNow(new Date(lastBackupAt), { addSuffix: true })}
          </p>
        )}

        <Button onClick={handleExport} className="w-full" size="sm" disabled={loading || (exportEncrypted && !hasRecoveryKey)} data-testid="export-backup-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
          {isFiltered ? 'Export Filtered Backup' : 'Export Full Backup'}
        </Button>

        {exportEncrypted && !hasRecoveryKey && (
          <p className="text-xs text-muted-foreground/70 text-center">Generate a recovery key first to enable encrypted backups</p>
        )}
      </div>
    </>
  );
}
